import React, { useEffect } from 'react';
import { usePlpDatalayer } from '../../../integrations/datalayer/hooks/usePlpDatalayer';
import { DEFAULT_PAGE_SIZE, NUMBER_OF_VARIANTS_PER_PRODUCT_IN_PLP } from '../../../integrations/relewise/const';
import { useCategoryViewTracker } from '../../../integrations/relewise/hooks/useCategoryViewTracker';
import { parseProductListSortOption } from '../../../utils/productListSort';
import { DEFAULT_MOBILE_COLUMN_COUNT, PRODUCT_LIST_MODULE_NAME, TOGGLE_VIEW_QUERY_PARAM } from '../cus-product-list.const';
import { ColumnCount, ICusProductListViewProps } from '../cus-product-list.data';
import useFilters from './useFilters';

const parseToggleViewQueryValue = (value: string | undefined): ColumnCount => {
    if (value === '1') {
        return 1;
    }
    if (value === '2') {
        return 2;
    }
    return DEFAULT_MOBILE_COLUMN_COUNT;
};

export default function useProductList(props: ICusProductListViewProps) {
    const categoryRecordId = props.context.request.urlTokens.recordId ?? '';
    const q = (props.context.request.query?.q ?? '').trim();

    const isCategoryMode = categoryRecordId.length > 0;
    const isSearchMode = !isCategoryMode && q.length > 0;
    const isSearchPage = !isCategoryMode;
    const isEmptySearch = isSearchPage && !isSearchMode;

    const categoryProducts = props.data.categoryProducts.result;
    const searchProducts = props.data.searchProducts.result;

    const itemsPerPage = props.config.itemsPerPage ?? DEFAULT_PAGE_SIZE;
    const variantsPerProduct = props.config.variantsPerProduct ?? NUMBER_OF_VARIANTS_PER_PRODUCT_IN_PLP;
    const initialSort = parseProductListSortOption(props.context.request.query?.sort);
    const filtersConfig = isSearchMode
        ? ({ mode: 'search', query: q, itemsPerPage, variantsPerProduct, initialSort } as const)
        : ({ mode: 'category', categoryRecordId, itemsPerPage, variantsPerProduct, initialSort } as const);
    const [mobileColumns, setMobileColumns] = React.useState<ColumnCount>(
        parseToggleViewQueryValue(props.context.request.query?.[TOGGLE_VIEW_QUERY_PARAM])
    );
    const { filteredResults, isLoading, search, goToPage, setSort, applyFiltersAndSort, currentSort } = useFilters(
        props.context.actionContext,
        filtersConfig
    );

    const handleMobileColumnsChange = React.useCallback((columns: ColumnCount) => {
        setMobileColumns(columns);

        if (typeof window === 'undefined') {
            return;
        }

        const params = new URLSearchParams(window.location.search);
        if (columns === DEFAULT_MOBILE_COLUMN_COUNT) {
            params.delete(TOGGLE_VIEW_QUERY_PARAM);
        } else {
            params.set(TOGGLE_VIEW_QUERY_PARAM, String(columns));
        }

        const queryString = params.toString();
        const newUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}${window.location.hash}`;
        window.history.replaceState(window.history.state, '', newUrl);
    }, []);

    const baseProducts = isSearchMode ? searchProducts : categoryProducts;
    const activeProducts = filteredResults ?? baseProducts;
    const hasNoResults = !isEmptySearch && activeProducts !== undefined && activeProducts.productList.length === 0;

    const hasFiredEmptyCategoryPlpLog = React.useRef(false);
    useEffect(
        function logEmptyCategoryPlp() {
            // Only log empty category if there are no filters applied
            const queryKeys = Object.keys(props.context.request.query ?? {});
            const hasFilters = queryKeys.some(k => k !== 'page' && k !== 'sort' && k !== TOGGLE_VIEW_QUERY_PARAM);
            if (hasFilters) {
                hasFiredEmptyCategoryPlpLog.current = true;
                return;
            }

            const hasUnfilteredProducts = (baseProducts?.productList.length ?? 0) > 0;
            if (isCategoryMode && !hasUnfilteredProducts && !hasFiredEmptyCategoryPlpLog.current) {
                let categorySlug;
                try {
                    categorySlug = decodeURIComponent(props.context.request.urlTokens?.categories?.[0] ?? '');
                } catch {
                    categorySlug = props.context.request.urlTokens?.categories?.[0] ?? '';
                }

                hasFiredEmptyCategoryPlpLog.current = true;
                const message = `[${PRODUCT_LIST_MODULE_NAME}] Category PLP has no results for categorySlug ${categorySlug} with categoryRecordId ${categoryRecordId}`;
                props.context.telemetry.error(message);
                console.error(message);
            }
        },
        [
            baseProducts,
            isCategoryMode,
            categoryRecordId,
            props.context.telemetry,
            props.context.request.query,
            props.context.request.urlTokens?.categories
        ]
    );

    const totalItems = activeProducts?.totalItems ?? 0;
    const totalPages = activeProducts?.totalPages ?? 0;
    const currentPage = activeProducts?.currentPage ?? 1;
    const currentIndex = (currentPage - 1) * itemsPerPage + 1;

    const productCountText =
        totalItems === 1 ? props.resources.oneProduct : props.resources.numberOfProducts.replace('{0}', totalItems.toString());

    const pageTitle = isSearchMode ? `"${q.toUpperCase()}"` : baseProducts?.categoryDisplayName;
    const categoryRelewiseId = categoryProducts?.relewiseCategoryId;

    const plpRef = React.useRef<HTMLDivElement>(null);
    const lastScrolledPage = React.useRef(currentPage);

    usePlpDatalayer({
        request: props.context.request,
        telemetry: props.context.telemetry,
        activeProducts,
        category: props.data.currentCategoryData.result?.currentCategory ?? undefined,
        isSearchMode,
        currentIndex
    });

    useCategoryViewTracker(categoryRelewiseId, props.context.request, props.context.telemetry, true);

    React.useEffect(() => {
        if (lastScrolledPage.current === currentPage) {
            return;
        }
        lastScrolledPage.current = currentPage;
        const raf = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const el = plpRef.current;
                if (!el) {
                    return;
                }
                const top = el.getBoundingClientRect().top + window.scrollY;
                window.scrollTo({ top, behavior: 'smooth' });
            });
        });
        return () => cancelAnimationFrame(raf);
    }, [currentPage]);
    return {
        isSearchPage,
        isSearchMode,
        isEmptySearch,
        hasNoResults,
        activeProducts,
        totalItems,
        totalPages,
        currentPage,
        itemsPerPage,
        productCountText,
        pageTitle,
        mobileColumns,
        handleSortChange: setSort,
        setMobileColumns: handleMobileColumnsChange,
        search,
        applyFiltersAndSort,
        isLoading,
        handlePageChange: goToPage,
        plpRef,
        categoryRecordId,
        currentSort,
        currentIndex
    };
}
